import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../utils/firebase';
import { collection, addDoc, Timestamp, serverTimestamp, getDoc, doc } from 'firebase/firestore';
import { useAuth } from '../hooks/useAuth';
import Notification from './Notification';

interface Memory {
  id?: string;
  userId: string;
  title: string;
  content: string;
  tags: string[];
  isPublic: boolean;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
  authorName: string;
  userIcon: string;
}

interface Notification {
  message: string;
  type: 'success' | 'error';
  onClose: () => void;
}

const CreateDreamForm: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [tags, setTags] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState<Notification | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    
    setIsSubmitting(true);

    try {
      // Get the user's current display name from Firestore
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const userDisplayName = userDoc.exists() ? userDoc.data().displayName : (user.displayName || 'Anonymous');

      const dreamData: Omit<Memory, 'id'> = {
        title,
        content,
        tags: tags.split(',').map(tag => tag.trim()).filter(tag => tag !== ''),
        isPublic,
        createdAt: serverTimestamp() as Timestamp,
        updatedAt: serverTimestamp() as Timestamp,
        userId: user.uid,
        authorName: userDisplayName
      };

      console.log('Creating new dream with data:', dreamData);
      await addDoc(collection(db, 'dreams'), dreamData);
      setNotification({
        message: 'Dream recorded successfully!',
        type: 'success',
        onClose: () => setNotification(null)
      });

      // Navigate to the user's profile page after successful creation
      setTimeout(() => {
        navigate(`/profile/${user.uid}`);
      }, 1500);
    } catch (error) {
      console.error('Error recording dream:', error);
      setNotification({
        message: 'Failed to record dream. Please try again.',
        type: 'error',
        onClose: () => setNotification(null)
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-4 sm:p-6">
      <div className="text-center mb-8">
        <h1 className="text-3xl sm:text-4xl font-bold text-dream-50 mb-2
                     bg-clip-text text-transparent bg-gradient-to-r from-dream-300 to-dream-500
                     animate-pulse">
          Record a New Dream
        </h1>
        <p className="text-dream-300/80 text-sm sm:text-base">
          Capture the magic of your dreams and share them with the world
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
        <div>
          <label htmlFor="title" className="block text-sm font-medium text-dream-200 mb-2">
            Title
          </label>
          <input
            type="text"
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full px-3 sm:px-4 py-2 rounded-lg bg-dream-800/50 border border-dream-700/30
                     text-dream-50 placeholder-dream-400/50 focus:outline-none focus:ring-2
                     focus:ring-dream-500/50 focus:border-dream-600/50 transition-all duration-300
                     hover:border-dream-600/50"
            placeholder="Give your dream a title..."
            required
          />
        </div>

        <div>
          <label htmlFor="content" className="block text-sm font-medium text-dream-200 mb-2">
            Content
          </label>
          <textarea
            id="content"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            className="w-full px-3 sm:px-4 py-2 rounded-lg bg-dream-800/50 border border-dream-700/30
                     text-dream-50 placeholder-dream-400/50 focus:outline-none focus:ring-2
                     focus:ring-dream-500/50 focus:border-dream-600/50 transition-all duration-300
                     hover:border-dream-600/50 resize-none min-h-[200px]"
            placeholder="Describe your dream..."
            required
          />
        </div>

        <div>
          <label htmlFor="tags" className="block text-sm font-medium text-dream-200 mb-2">
            Tags (comma separated)
          </label>
          <input
            type="text"
            id="tags"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            className="w-full px-3 sm:px-4 py-2 rounded-lg bg-dream-800/50 border border-dream-700/30
                     text-dream-50 placeholder-dream-400/50 focus:outline-none focus:ring-2
                     focus:ring-dream-500/50 focus:border-dream-600/50 transition-all duration-300
                     hover:border-dream-600/50"
            placeholder="Add tags to help others find your dream..."
          />
        </div>

        <div className="flex items-center justify-between p-4 rounded-lg bg-dream-800/50 border border-dream-700/50">
          <label htmlFor="isPublic" className="flex items-center gap-3 cursor-pointer w-full">
            <div className="relative">
              <input
                type="checkbox"
                id="isPublic"
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
                className="sr-only"
              />
              <div className={`block w-14 h-8 rounded-full transition-colors duration-300 ${
                isPublic ? 'bg-dream-500/50' : 'bg-dream-700/50'
              }`}>
                <div className={`absolute left-1 top-1 w-6 h-6 rounded-full transition-all duration-300
                              flex items-center justify-center ${
                  isPublic 
                    ? 'bg-dream-500 transform translate-x-6' 
                    : 'bg-dream-700'
                }`}>
                  {isPublic ? (
                    <svg className="w-4 h-4 text-dream-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4 text-dream-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  )}
                </div>
              </div>
            </div>
            <div>
              <span className="text-dream-200 text-sm">Share this dream</span>
              <p className="text-dream-400 text-xs">
                {isPublic ? 'Your dream will be visible to other dreamers' : 'Only you can see this dream'}
              </p>
            </div>
          </label>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 pt-2">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="w-full sm:w-auto px-4 py-2 rounded-lg bg-dream-800/50 text-dream-200 
                     hover:bg-dream-800/70 transition-all duration-300
                     border border-dream-700/30 hover:border-dream-600/50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full sm:w-auto px-4 py-2 rounded-lg bg-dream-600/50 text-dream-50 
                     hover:bg-dream-600/70 transition-all duration-300
                     border border-dream-500/30 hover:border-dream-400/50
                     disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Creating...
              </span>
            ) : (
              'Create Dream'
            )}
          </button>
        </div>
      </form>

      {notification && (
        <div className="fixed top-4 right-4 z-50">
          <Notification
            message={notification.message}
            type={notification.type}
            onClose={notification.onClose}
          />
        </div>
      )}
    </div>
  );
};

export default CreateDreamForm; 